import { useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../data/api';
import type { Lead, LeadChannel, LeadStatus, LeadTouch, MessageTemplate } from '../data/types';

interface Props {
  lead: Lead;
  templates: MessageTemplate[];
  onClose: () => void;
  onChange: (l: Lead) => void;
  onDelete: (id: string) => void;
  onConvert: (l: Lead) => void;
}

const STATUSES: { id: LeadStatus; label: string }[] = [
  { id: 'new', label: 'Nuovo' },
  { id: 'contacted', label: 'Contattato' },
  { id: 'replied', label: 'Risposto' },
  { id: 'meeting', label: 'Incontro' },
  { id: 'won', label: 'Vinto' },
  { id: 'lost', label: 'Perso' },
];

const CHANNEL_ICON: Record<LeadChannel, 'message' | 'globe' | 'check'> = {
  whatsapp: 'message',
  email: 'globe',
  call: 'check',
  note: 'check',
};

const fillVars = (text: string, l: Lead) =>
  text
    .replaceAll('{nome}', l.name)
    .replaceAll('{citta}', l.locality ?? '')
    .replaceAll('{categoria}', l.category ?? '');

const phoneDigits = (p: string | null) => (p ?? '').replace(/[^0-9]/g, '');

export function LeadDrawer({ lead, templates, onClose, onChange, onDelete, onConvert }: Props) {
  const [touches, setTouches] = useState<LeadTouch[]>([]);
  const [channel, setChannel] = useState<LeadChannel>(lead.phone ? 'whatsapp' : 'email');
  const [templateId, setTemplateId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  // editable detail fields
  const [edit, setEdit] = useState({
    category: lead.category ?? '',
    locality: lead.locality ?? '',
    phone: lead.phone ?? '',
    email: lead.email ?? '',
    website: lead.website ?? '',
    instagram: lead.instagram ?? '',
    notes: lead.notes ?? '',
    nextFollowUp: lead.nextFollowUp ?? '',
  });

  useEffect(() => {
    void api.leadTouches(lead.id).then(setTouches).catch(() => {});
  }, [lead.id]);

  const channelTemplates = useMemo(() => templates.filter((t) => t.channel === channel), [templates, channel]);

  // When channel changes, pick its first template and fill it.
  useEffect(() => {
    const first = channelTemplates[0];
    if (first) {
      setTemplateId(first.id);
      setBody(fillVars(first.body, lead));
      setSubject(first.subject ? fillVars(first.subject, lead) : '');
    } else {
      setTemplateId('');
      setBody('');
      setSubject('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setBody(fillVars(t.body, lead));
      setSubject(t.subject ? fillVars(t.subject, lead) : '');
    }
  };

  const setStatus = async (status: LeadStatus) => {
    setBusy('status');
    try {
      onChange(await api.patchLead(lead.id, { status }));
    } finally {
      setBusy(null);
    }
  };

  const saveDetails = async () => {
    setBusy('details');
    try {
      onChange(
        await api.patchLead(lead.id, {
          category: edit.category || null,
          locality: edit.locality || null,
          phone: edit.phone || null,
          email: edit.email || null,
          website: edit.website || null,
          instagram: edit.instagram || null,
          notes: edit.notes || null,
          nextFollowUp: edit.nextFollowUp || null,
        }),
      );
    } finally {
      setBusy(null);
    }
  };

  /** Optimistically reflect that the lead was just contacted. */
  const reflectContacted = () => {
    onChange({
      ...lead,
      lastContactedAt: new Date().toISOString(),
      status: lead.status === 'new' ? 'contacted' : lead.status,
    });
  };
  const reloadTouches = () => void api.leadTouches(lead.id).then(setTouches).catch(() => {});

  const openWhatsApp = async () => {
    const digits = phoneDigits(edit.phone || lead.phone);
    if (!digits) return;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(body)}`, '_blank', 'noopener');
    setBusy('wa');
    try {
      await api.addTouch(lead.id, { channel: 'whatsapp', note: body.slice(0, 90) });
      reflectContacted();
      reloadTouches();
    } finally {
      setBusy(null);
    }
  };

  const sendEmail = async () => {
    if (!(edit.email || lead.email)) return;
    setBusy('email');
    try {
      await api.sendLeadEmail(lead.id, { subject, body });
      reflectContacted();
      reloadTouches();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Invio non riuscito: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const copy = () => {
    void navigator.clipboard?.writeText(channel === 'email' && subject ? `${subject}\n\n${body}` : body);
  };

  // Apre Google Maps già compilato col nome + comune dell'attività: il numero
  // (spesso assente in OpenStreetMap), l'indirizzo e gli orari si trovano in un
  // clic, senza ridigitare la ricerca. Il comune sta in coda a locality, che a
  // volte è tipo "Centro storico · Monopoli" → tengo l'ultimo pezzo.
  const openMaps = () => {
    const comune = (edit.locality || lead.locality || '').split('·').pop()?.trim() ?? '';
    const place = [lead.name, comune].filter(Boolean).join(' ');
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place)}`, '_blank', 'noopener');
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!confirm(`Eliminare il lead "${lead.name}"?`)) return;
    await api.deleteLead(lead.id);
    onDelete(lead.id);
  };

  return (
    <div className="dr-scrim open" onMouseDown={onClose}>
      <aside className="lead-dr" onMouseDown={(e) => e.stopPropagation()}>
        <div className="lead-dr-head">
          <div style={{ minWidth: 0 }}>
            <div className="lead-dr-name">{lead.name}</div>
            <div className="rs-cli-meta">{[lead.category, lead.locality].filter(Boolean).join(' · ') || '—'}</div>
          </div>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="lead-dr-body">
          {/* status */}
          <div className="lead-seg" role="group" aria-label="Stato">
            {STATUSES.map((s) => (
              <button key={s.id} type="button" className={lead.status === s.id ? 'on' : ''} disabled={busy === 'status'} onClick={() => setStatus(s.id)}>
                {s.label}
              </button>
            ))}
          </div>

          {/* compose */}
          <div className="rs-card" style={{ padding: 14 }}>
            {lead.lastContactedAt && (
              <div className="rs-muted" style={{ fontSize: 12, marginBottom: 8 }}>
                ⏱ Ultimo contatto {shortDateTime(lead.lastContactedAt)} — evita di ricontattare troppo spesso.
              </div>
            )}
            <div className="lead-ch">
              <button type="button" className={channel === 'whatsapp' ? 'on' : ''} onClick={() => setChannel('whatsapp')}>
                <Icon name="message" size={15} /> WhatsApp
              </button>
              <button type="button" className={channel === 'email' ? 'on' : ''} onClick={() => setChannel('email')}>
                <Icon name="globe" size={15} /> Email
              </button>
            </div>

            {channelTemplates.length > 0 ? (
              <select className="input" value={templateId} onChange={(e) => pickTemplate(e.target.value)} style={{ marginBottom: 10 }}>
                {channelTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            ) : (
              <div className="rs-muted" style={{ fontSize: 12.5, marginBottom: 8 }}>Nessuno script per questo canale.</div>
            )}

            {channel === 'email' && (
              <input className="input" value={subject} placeholder="Oggetto" onChange={(e) => setSubject(e.target.value)} style={{ marginBottom: 8 }} />
            )}
            <textarea className="input" rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Il tuo messaggio…" />

            <div className="lead-actions">
              {channel === 'whatsapp' ? (
                <button type="button" className="btn btn-primary" disabled={!phoneDigits(edit.phone || lead.phone) || busy === 'wa'} onClick={openWhatsApp}>
                  <Icon name="message" size={15} /> Apri WhatsApp
                </button>
              ) : (
                <button type="button" className="btn btn-primary" disabled={!(edit.email || lead.email) || busy === 'email'} onClick={sendEmail}>
                  <Icon name="globe" size={15} /> {busy === 'email' ? 'Invio…' : 'Invia email'}
                </button>
              )}
              <button type="button" className="btn btn-ghost" onClick={copy}>Copia</button>
            </div>
            {channel === 'whatsapp' && !phoneDigits(edit.phone || lead.phone) && (
              <div className="rs-muted" style={{ fontSize: 12 }}>
                Manca il numero —{' '}
                <button
                  type="button"
                  onClick={openMaps}
                  style={{ font: 'inherit', color: 'inherit', textDecoration: 'underline', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                >
                  cercalo su Google Maps
                </button>
                , poi incollalo qui nei Dettagli.
              </div>
            )}
          </div>

          {/* details */}
          <div className="rs-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <div className="rs-card-title">Dettagli</div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12.5, padding: '5px 10px', whiteSpace: 'nowrap' }}
                onClick={openMaps}
                title="Apri l'attività su Google Maps: numero, indirizzo e orari in un clic"
              >
                <Icon name="globe" size={14} /> Cerca su Google Maps
              </button>
            </div>
            <div className="lead-grid">
              <Field label="Tipo" v={edit.category} on={(v) => setEdit({ ...edit, category: v })} />
              <Field label="Città" v={edit.locality} on={(v) => setEdit({ ...edit, locality: v })} />
              <Field label="Telefono" v={edit.phone} on={(v) => setEdit({ ...edit, phone: v })} />
              <Field label="Email" v={edit.email} on={(v) => setEdit({ ...edit, email: v })} />
              <Field label="Sito attuale" v={edit.website} on={(v) => setEdit({ ...edit, website: v })} />
              <Field label="Instagram" v={edit.instagram} on={(v) => setEdit({ ...edit, instagram: v })} />
              <Field label="Prossimo follow-up" v={edit.nextFollowUp} on={(v) => setEdit({ ...edit, nextFollowUp: v })} placeholder="2026-06-20" />
            </div>
            <label className="lead-lbl" style={{ marginTop: 8 }}>Note</label>
            <textarea className="input" rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-ghost" disabled={busy === 'details'} onClick={saveDetails}>
                {busy === 'details' ? 'Salvo…' : 'Salva dettagli'}
              </button>
            </div>
          </div>

          {/* history */}
          <div className="rs-card" style={{ padding: 14 }}>
            <div className="rs-card-title" style={{ marginBottom: 10 }}>Storico contatti</div>
            {touches.length ? (
              <div className="rs-stack" style={{ gap: 8 }}>
                {touches.map((t) => (
                  <div key={t.id} className="lead-touch">
                    <Icon name={CHANNEL_ICON[t.channel]} size={14} />
                    <span style={{ flex: 1, minWidth: 0 }}>{t.note || t.channel}</span>
                    <span className="rs-dim" style={{ fontSize: 11.5 }}>{shortDateTime(t.createdAt)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rs-muted" style={{ fontSize: 13 }}>Ancora nessun contatto.</div>
            )}
          </div>

          {/* footer actions */}
          <div className="lead-foot">
            <button type="button" className="btn btn-primary" onClick={() => onConvert(lead)}>
              <Icon name="building" size={15} /> Converti in cliente
            </button>
            <button type="button" className="rs-logout" style={{ width: 'auto', margin: 0, color: 'var(--danger)', borderColor: 'var(--danger-line)' }} onClick={remove}>
              Elimina
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, v, on, placeholder }: { label: string; v: string; on: (v: string) => void; placeholder?: string }) {
  return (
    <label className="lead-field">
      <span className="lead-lbl">{label}</span>
      <input className="input" value={v} placeholder={placeholder} onChange={(e) => on(e.target.value)} />
    </label>
  );
}

function shortDateTime(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'oggi';
  if (days === 1) return 'ieri';
  return `${days} g fa`;
}
